// FiltroFechaUI.jsx

import React, { useMemo } from 'react';
import moment from 'moment';

const MESES = [
    { valor: "01", nombre: "Enero" },
    { valor: "02", nombre: "Febrero" },
    { valor: "03", nombre: "Marzo" },
    { valor: "04", nombre: "Abril" },
    { valor: "05", nombre: "Mayo" },
    { valor: "06", nombre: "Junio" },
    { valor: "07", nombre: "Julio" },
    { valor: "08", nombre: "Agosto" },
    { valor: "09", nombre: "Septiembre" },
    { valor: "10", nombre: "Octubre" },
    { valor: "11", nombre: "Noviembre" },
    { valor: "12", nombre: "Diciembre" },
];

const FiltroFechaUI = ({
    filtro, setFiltro,
    fechaSeleccionada, setFechaSeleccionada,
    horaSeleccionada, setHoraSeleccionada,
    semanaSeleccionada, setSemanaSeleccionada,
    mesSeleccionado, setMesSeleccionado,
    anioSeleccionado, setAnioSeleccionado,
    handleApplyFilter,
    maxAnio = moment().year()
}) => {
    const anioActual = moment().year();

    const listaAnios = useMemo(() => {
        const anos = [];
        const fin = Math.max(maxAnio || anioActual, anioActual);
        for (let a = fin; a >= 2020; a--) {
            anos.push(a);
        }
        return anos;
    }, [maxAnio, anioActual]);

    // Extraer año y número de semana de semanaSeleccionada (ej: 2026-W05)
    const [anioSemana, numSemana] = useMemo(() => {
        if (semanaSeleccionada && semanaSeleccionada.includes('-W')) {
            const partes = semanaSeleccionada.split('-W');
            return [parseInt(partes[0], 10) || anioActual, parseInt(partes[1], 10) || 1];
        }
        return [anioActual, moment().isoWeek()];
    }, [semanaSeleccionada, anioActual]);

    // Lista completa de semanas para el año seleccionado
    const listaSemanas = useMemo(() => {
        const semanas = [];
        const totalSemanas = moment().year(anioSemana).isoWeeksInYear();
        for (let w = 1; w <= totalSemanas; w++) {
            const m = moment().year(anioSemana).isoWeek(w);
            const ini = m.clone().startOf('isoWeek').format('DD/MM');
            const fin = m.clone().endOf('isoWeek').format('DD/MM');
            semanas.push({
                valor: `${anioSemana}-W${String(w).padStart(2, '0')}`,
                numero: w,
                etiqueta: `Semana ${w} (${ini} al ${fin})`
            });
        }
        return semanas;
    }, [anioSemana]);

    // Extraer año y mes del mesSeleccionado (ej: 2026-03)
    const [anioMes, valMes] = useMemo(() => {
        if (mesSeleccionado && mesSeleccionado.includes('-')) {
            const partes = mesSeleccionado.split('-');
            return [parseInt(partes[0], 10) || anioActual, partes[1] || "01"];
        }
        return [anioActual, moment().format('MM')];
    }, [mesSeleccionado, anioActual]);

    const handleCambiarAnioSemana = (nuevoAnio) => {
        const nuevo = `${nuevoAnio}-W${String(numSemana).padStart(2, '0')}`;
        setSemanaSeleccionada(nuevo);
    };

    const handleSeleccionarSemanaPorCalendario = (e) => {
        const fecha = e.target.value;
        if (fecha) {
            const nuevaSemana = moment(fecha).format('GGGG-[W]WW');
            setSemanaSeleccionada(nuevaSemana);
        }
    };

    const handleCambiarMes = (mesVal) => {
        setMesSeleccionado(`${anioMes}-${mesVal}`);
    };

    const handleCambiarAnioMes = (nuevoAnio) => {
        setMesSeleccionado(`${nuevoAnio}-${valMes}`);
    };

    return (
        <div className="filtro-fecha-contenedor p-2">
            {/* 1. Selector de Tipo de Período (Botones tipo Pill) */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 pb-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <i className="fas fa-filter text-primary"></i>
                    <span className="fw-bold small text-uppercase text-secondary">Tipo de Período:</span>
                </div>
                <div className="btn-group btn-group-sm p-1 bg-light border rounded-pill shadow-none flex-wrap">
                    <button
                        type="button"
                        className={`btn btn-sm rounded-pill px-3 fw-bold transition-all ${filtro === "dia" ? "btn-primary shadow-sm" : "btn-light text-dark"}`}
                        onClick={() => setFiltro("dia")}
                    >
                        <i className="fas fa-calendar-day me-1"></i> Día
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm rounded-pill px-3 fw-bold transition-all ${filtro === "dia-hora" ? "btn-primary shadow-sm" : "btn-light text-dark"}`}
                        onClick={() => setFiltro("dia-hora")}
                    >
                        <i className="fas fa-clock me-1"></i> Hora
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm rounded-pill px-3 fw-bold transition-all ${filtro === "semana" ? "btn-primary shadow-sm" : "btn-light text-dark"}`}
                        onClick={() => setFiltro("semana")}
                    >
                        <i className="fas fa-calendar-week me-1"></i> Semana
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm rounded-pill px-3 fw-bold transition-all ${filtro === "mes" ? "btn-primary shadow-sm" : "btn-light text-dark"}`}
                        onClick={() => setFiltro("mes")}
                    >
                        <i className="fas fa-calendar-alt me-1"></i> Mes
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm rounded-pill px-3 fw-bold transition-all ${filtro === "año" ? "btn-primary shadow-sm" : "btn-light text-dark"}`}
                        onClick={() => setFiltro("año")}
                    >
                        <i className="fas fa-calendar me-1"></i> Año
                    </button>
                </div>
            </div>

            {/* 2. Tarjeta con los Selectores Específicos del Filtro */}
            <div className="card border border-secondary-subtle bg-light shadow-sm rounded-3 p-3 mb-3">
                {/* Selector para Día o Día-Hora */}
                {(filtro === "dia" || filtro === "dia-hora") && (
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-md-6 col-lg-4">
                            <label htmlFor="fechaSelect" className="form-label small fw-bold text-muted text-uppercase mb-1">
                                <i className="fas fa-calendar-day text-primary me-1"></i> Fecha:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="far fa-calendar-alt text-primary"></i>
                                </span>
                                <input
                                    type="date"
                                    id="fechaSelect"
                                    className="form-control form-control-sm border-start-0"
                                    value={fechaSeleccionada}
                                    onChange={(e) => setFechaSeleccionada(e.target.value)}
                                />
                            </div>
                        </div>

                        {filtro === "dia-hora" && (
                            <div className="col-12 col-md-6 col-lg-3">
                                <label htmlFor="horaSelect" className="form-label small fw-bold text-muted text-uppercase mb-1">
                                    <i className="fas fa-clock text-primary me-1"></i> Hora:
                                </label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="far fa-clock text-primary"></i>
                                    </span>
                                    <input
                                        type="time"
                                        id="horaSelect"
                                        className="form-control form-control-sm border-start-0"
                                        value={horaSeleccionada}
                                        onChange={(e) => setHoraSeleccionada(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Selector para Semana */}
                {filtro === "semana" && (
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-sm-4 col-md-3">
                            <label htmlFor="anioSemanaSelect" className="form-label small fw-bold text-muted text-uppercase mb-1">
                                <i className="fas fa-calendar text-primary me-1"></i> Año:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="far fa-calendar-alt text-muted"></i>
                                </span>
                                <select
                                    id="anioSemanaSelect"
                                    className="form-select form-select-sm border-start-0 fw-semibold"
                                    value={anioSemana}
                                    onChange={(e) => handleCambiarAnioSemana(e.target.value)}
                                >
                                    {listaAnios.map((a) => (
                                        <option key={a} value={a}>{a}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-12 col-sm-8 col-md-5">
                            <label htmlFor="semanaDropdown" className="form-label small fw-bold text-muted text-uppercase mb-1">
                                <i className="fas fa-calendar-week text-primary me-1"></i> Semana del Año:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="fas fa-list-ol text-primary"></i>
                                </span>
                                <select
                                    id="semanaDropdown"
                                    className="form-select form-select-sm border-start-0 fw-semibold text-primary"
                                    value={semanaSeleccionada}
                                    onChange={(e) => setSemanaSeleccionada(e.target.value)}
                                >
                                    {listaSemanas.map((s) => (
                                        <option key={s.valor} value={s.valor}>
                                            {s.etiqueta}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-12 col-md-4">
                            <label htmlFor="calendarioSemana" className="form-label small fw-bold text-muted text-uppercase mb-1">
                                <i className="fas fa-mouse-pointer text-info me-1"></i> O elegir día:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="fas fa-calendar-day text-info"></i>
                                </span>
                                <input
                                    type="date"
                                    id="calendarioSemana"
                                    className="form-control form-control-sm border-start-0"
                                    title="Haz clic para seleccionar la semana correspondiente a este día"
                                    onChange={handleSeleccionarSemanaPorCalendario}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Selector para Mes */}
                {filtro === "mes" && (
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-sm-6 col-md-4">
                            <label htmlFor="mesDropdown" className="form-label small fw-bold text-muted text-uppercase mb-1">
                                <i className="fas fa-calendar-alt text-primary me-1"></i> Mes:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="far fa-calendar text-primary"></i>
                                </span>
                                <select
                                    id="mesDropdown"
                                    className="form-select form-select-sm border-start-0 fw-semibold text-primary"
                                    value={valMes}
                                    onChange={(e) => handleCambiarMes(e.target.value)}
                                >
                                    {MESES.map((m) => (
                                        <option key={m.valor} value={m.valor}>{m.nombre}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-12 col-sm-6 col-md-3">
                            <label htmlFor="anioMesDropdown" className="form-label small fw-bold text-muted text-uppercase mb-1">
                                <i className="fas fa-calendar text-primary me-1"></i> Año:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="far fa-calendar-alt text-muted"></i>
                                </span>
                                <select
                                    id="anioMesDropdown"
                                    className="form-select form-select-sm border-start-0 fw-semibold"
                                    value={anioMes}
                                    onChange={(e) => handleCambiarAnioMes(e.target.value)}
                                >
                                    {listaAnios.map((a) => (
                                        <option key={a} value={a}>{a}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {/* Selector para Año */}
                {filtro === "año" && (
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-sm-6 col-md-3">
                            <label htmlFor="anioSelect" className="form-label small fw-bold text-muted text-uppercase mb-1">
                                <i className="fas fa-calendar text-primary me-1"></i> Año:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="far fa-calendar-alt text-primary"></i>
                                </span>
                                <select
                                    id="anioSelect"
                                    className="form-select form-select-sm border-start-0 fw-semibold text-primary"
                                    value={anioSeleccionado}
                                    onChange={(e) => setAnioSeleccionado(e.target.value)}
                                >
                                    {listaAnios.map((a) => (
                                        <option key={a} value={a}>{a}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 3. Botón Aplicar Filtros */}
            <div className="d-flex justify-content-end">
                <button
                    type="button"
                    className="btn btn-primary btn-sm px-4 py-2 rounded-pill shadow-sm fw-bold d-inline-flex align-items-center gap-2"
                    onClick={handleApplyFilter}
                >
                    <i className="fas fa-filter"></i>
                    <span>Aplicar Filtros</span>
                </button>
            </div>
        </div>
    );
};

export default FiltroFechaUI;
